// Runtime config — see docs/RELEASE_FLOW.md §13.
//
// Each env's GitHub Environment variables populate /config.json at deploy
// time; the SPA fetches it at startup. Local dev / tests fall back to
// `import.meta.env.VITE_*` so `pnpm run dev` and unit tests keep working
// without serving config.json.
//
// Exports are live `let` bindings: when loadRuntimeConfig() reassigns
// them, every `import { env, ... } from '@/config'` consumer transparently
// sees the new value (per the ES module spec). The internal `commit()`
// helper is the only writer.

import * as Sentry from '@sentry/react';
import { z } from 'zod';

// Shape of /config.json as deployed alongside the SPA bundle.
// `.strict()` rejects unknown fields so a typo in the deploy step's jq
// (e.g. `docusgnClientId`) surfaces as a validation error instead of
// silently leaving the SPA on the build-time default.
//
// `environment` and `tag` are emitted unconditionally by v1_cd-deploy.yaml's
// `Inject env-scoped config.json` step (its base jq filter is
// `{environment:$environment, tag:$tag}`). Without `tag` here, every V1
// /config.json fails strict validation and the SPA silently falls back to
// build-time defaults.
const remoteConfigSchema = z.object({
  environment: z.string().optional(),
  tag: z.string().optional(),
  apiUrl: z.string().optional(),
  cueingAppUrl: z.string().optional(),
  apiTimeout: z.union([ z.string(), z.number() ]).optional(),
  completeFileUploadTimeout: z.union([ z.string(), z.number() ]).optional(),
  docusignClientId: z.string().optional(),
  docusignJsUrl: z.string().optional(),
  cognitoDomain: z.string().optional(),
  cognitoClientId: z.string().optional(),
  oktaDomain: z.string().optional(),
}).strict();

type RemoteConfig = z.infer<typeof remoteConfigSchema>;

/** Fully-resolved config after build-time + remote merge. */
interface ResolvedConfig {
  env: string;
  isProduction: boolean;
  isLocal: boolean;
  apiUrl: string;
  cueingAppUrl: string;
  apiTimeout: number | undefined;
  completeFileUploadTimeout: number | undefined;
  docusignClientId: string;
  docusignJsUrl: string;
  cognitoDomain: string;
  cognitoClientId: string;
  oktaDomain: string;
}

const CONFIG_URL = '/config.json';
const DEFAULT_DOCUSIGN_JS_URL = 'https://js-d.docusign.com/bundle.js';

/** Coerce a possibly-undefined string|number to a number, or undefined. */
function toInt(v: string | number | undefined): number | undefined {
  if (v == null || v === '') return undefined;
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
}

/** Choose the build-time API URL based on proxy / local / remote mode. */
function pickBuildtimeApiUrl(env: string): string {
  if (import.meta.env.VITE_USE_PROXY_API === 'true') {
    return import.meta.env.VITE_PROXY_API_URL ?? '';
  }
  if (env === 'local') {
    return import.meta.env.VITE_LOCAL_API_URL ?? '';
  }
  return import.meta.env.VITE_API_URL ?? '';
}

/** Read VITE_* and produce a fully-resolved config (the dev/test path). */
function buildtimeDefaults(): ResolvedConfig {
  const env = import.meta.env.VITE_ENVIRONMENT ?? '';
  return {
    env,
    isProduction: env === 'production',
    isLocal: env === 'local',
    apiUrl: pickBuildtimeApiUrl(env),
    cueingAppUrl: import.meta.env.VITE_CUEING_APP_URL ?? '',
    apiTimeout: toInt(import.meta.env.VITE_API_TIMEOUT),
    completeFileUploadTimeout: toInt(import.meta.env.VITE_COMPLETE_FILE_UPLOAD_TIMEOUT),
    docusignClientId: import.meta.env.VITE_DOCUSIGN_CLIENT_ID ?? '',
    docusignJsUrl: import.meta.env.VITE_DOCUSIGN_JS_URL ?? DEFAULT_DOCUSIGN_JS_URL,
    cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN ?? '',
    cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
    oktaDomain: import.meta.env.VITE_OKTA_DOMAIN ?? '',
  };
}

/**
 * Pick a string field from /config.json on top of build-time default.
 * Treats both undefined and empty string as "not present" so an
 * empty/missing field in /config.json doesn't clobber a valid
 * build-time value.
 */
function pick(remote: string | undefined, base: string): string {
  return remote != null && remote !== '' ? remote : base;
}

/**
 * Merge a /config.json snapshot on top of build-time defaults.
 * See `pick()` for the string-field precedence rule.
 */
function applyRemote(base: ResolvedConfig, remote: RemoteConfig): ResolvedConfig {
  const env = pick(remote.environment, base.env);
  return {
    env,
    isProduction: env === 'production',
    isLocal: env === 'local',
    apiUrl: pick(remote.apiUrl, base.apiUrl),
    cueingAppUrl: pick(remote.cueingAppUrl, base.cueingAppUrl),
    apiTimeout: toInt(remote.apiTimeout) ?? base.apiTimeout,
    completeFileUploadTimeout: toInt(remote.completeFileUploadTimeout) ?? base.completeFileUploadTimeout,
    docusignClientId: pick(remote.docusignClientId, base.docusignClientId),
    docusignJsUrl: pick(remote.docusignJsUrl, base.docusignJsUrl),
    cognitoDomain: pick(remote.cognitoDomain, base.cognitoDomain),
    cognitoClientId: pick(remote.cognitoClientId, base.cognitoClientId),
    oktaDomain: pick(remote.oktaDomain, base.oktaDomain),
  };
}

const initial = buildtimeDefaults();

// Live bindings — see file header. `commit()` is the only writer.
export let env: string = initial.env;
export let isProduction: boolean = initial.isProduction;
export let isLocal: boolean = initial.isLocal;
export let apiUrl: string = initial.apiUrl;
export let cueingAppUrl: string = initial.cueingAppUrl;
export let apiTimeout: number | undefined = initial.apiTimeout;
export let completeFileUploadTimeout: number | undefined = initial.completeFileUploadTimeout;
export let docusignClientId: string = initial.docusignClientId;
export let docusignJsUrl: string = initial.docusignJsUrl;
export let cognitoDomain: string = initial.cognitoDomain;
export let cognitoClientId: string = initial.cognitoClientId;
export let oktaDomain: string = initial.oktaDomain;

/** Push a resolved config into the live-bound exports. */
function commit(c: ResolvedConfig): void {
  env = c.env;
  isProduction = c.isProduction;
  isLocal = c.isLocal;
  apiUrl = c.apiUrl;
  cueingAppUrl = c.cueingAppUrl;
  apiTimeout = c.apiTimeout;
  completeFileUploadTimeout = c.completeFileUploadTimeout;
  docusignClientId = c.docusignClientId;
  docusignJsUrl = c.docusignJsUrl;
  cognitoDomain = c.cognitoDomain;
  cognitoClientId = c.cognitoClientId;
  oktaDomain = c.oktaDomain;
}

let loaded = false;

/**
 * Fetch /config.json once and merge it into the exported bindings.
 * Safe to call multiple times — only the first call performs the fetch.
 * On fetch/parse failure, silently keeps the build-time values.
 * On schema-validation failure, logs to console + Sentry and falls back —
 * the build-time defaults are always valid, so a malformed /config.json
 * never breaks the SPA but does surface to operators.
 */
export async function loadRuntimeConfig(): Promise<void> {
  if (loaded) return;
  loaded = true;

  let remote: RemoteConfig | null = null;
  try {
    const res = await fetch(CONFIG_URL, { cache: 'no-store' });
    if (!res.ok) return;
    const parsed = remoteConfigSchema.safeParse(await res.json());
    if (!parsed.success) {
      const issues = parsed.error.issues
        .map(i => `${i.path.join('.') || '(root)'}: ${i.message}`)
        .join('; ');
      const msg = `Invalid /config.json — falling back to build-time defaults. Issues: ${issues}`;
      Sentry.captureException(new Error(msg));
      return;
    }
    remote = parsed.data;
  }
  catch {
    // Network/JSON-parse failure — fall back to build-time values silently.
  }
  if (!remote) return;

  commit(applyRemote(initial, remote));
}
